import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Wallet, ShoppingBag, Home as HomeIcon, Briefcase, Building2, FileCheck, Plane,
  GraduationCap, Grid3x3, PiggyBank, Landmark, Scale, ArrowRight, Sparkles, FileText,
  ArrowLeft, ExternalLink,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/Markdown";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const CAT_ICONS = { Wallet, ShoppingBag, Home: HomeIcon, Briefcase, Building2, FileCheck, Plane, GraduationCap, Grid3x3 };
const TOOL_ICONS = { PiggyBank, Landmark, Scale };

export default function Category() {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [article, setArticle] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    api.get(`/categories/${slug}`)
      .then(({ data }) => { if (mounted) setData(data); })
      .catch(() => { if (mounted) setNotFound(true); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug]);

  const launchTopic = (guide) => {
    const prompt = language === "hi" ? guide.prompt_hi : guide.prompt_en;
    navigate(`/?ask=${encodeURIComponent(prompt)}&mode=${guide.mode || "understand"}`);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
        </div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <main className="mx-auto flex max-w-3xl flex-1 flex-col items-center justify-center px-4 text-center">
          <p className="text-slate-500">{t("not_found")}</p>
          <Button asChild className="mt-4 rounded-full"><Link to="/">{t("go_home")}</Link></Button>
        </main>
        <Footer />
      </div>
    );
  }

  const { category, guides, tools, sources } = data;
  const Icon = CAT_ICONS[category.icon] || Grid3x3;
  const name = language === "hi" ? category.name_hi : category.name_en;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <button onClick={() => navigate("/#categories")} className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="category-back-btn">
          <ArrowLeft className="h-4 w-4" /> {t("nav_categories")}
        </button>

        <div className="flex items-center gap-4">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Icon className="h-7 w-7" />
          </span>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl" data-testid="category-title">{name}</h1>
        </div>

        {/* Related calculators */}
        {tools.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-lg font-bold text-slate-900">{t("category_tools")}</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {tools.map((tool) => {
                const TIcon = TOOL_ICONS[tool.icon] || Scale;
                return (
                  <Link key={tool.slug} to={`/calculators/${tool.slug}`} data-testid={`category-tool-${tool.slug}`}
                    className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                      <TIcon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900">{language === "hi" ? tool.name_hi : tool.name_en}</span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{language === "hi" ? tool.desc_hi : tool.desc_en}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Guides & topics */}
        <section className="mt-10">
          <h2 className="font-display text-lg font-bold text-slate-900">{t("category_guides")}</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {guides.map((guide) => {
              const title = language === "hi" ? guide.title_hi : guide.title_en;
              const summary = language === "hi" ? guide.summary_hi : guide.summary_en;
              const isArticle = guide.type === "article";
              return (
                <div key={guide.guide_id} data-testid={`category-guide-${guide.guide_id}`} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isArticle ? "bg-slate-100 text-slate-500" : "bg-primary/10 text-primary"}`}>
                      {isArticle ? <FileText className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                      {isArticle ? t("overview") : t("ai_topic")}
                    </span>
                  </div>
                  <h3 className="font-display mt-2 text-base font-semibold text-slate-900">{title}</h3>
                  <p className="mt-1 flex-1 text-sm leading-relaxed text-slate-500">{summary}</p>
                  <div className="mt-4">
                    {isArticle ? (
                      <Button variant="outline" size="sm" className="rounded-full" data-testid={`read-guide-${guide.guide_id}`} onClick={() => setArticle(guide)}>
                        {t("read_guide")}
                      </Button>
                    ) : (
                      <Button size="sm" className="gap-1.5 rounded-full" data-testid={`launch-topic-${guide.guide_id}`} onClick={() => launchTopic(guide)}>
                        {t("launch_topic")} <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Suggested sources */}
        {sources && sources.length > 0 && (
          <section className="mt-10 rounded-2xl bg-slate-50 p-5" data-testid="category-sources">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("suggested_sources")}</h2>
            <div className="mt-3 flex flex-wrap gap-3">
              {sources.filter((s) => /^https?:\/\//i.test(s.url)).map((s) => (
                <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-primary hover:border-primary/40">
                  {s.name} <ExternalLink className="h-3 w-3" />
                </a>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-400">{t("sources_note")}</p>
          </section>
        )}
      </main>
      <Footer />

      <Dialog open={!!article} onOpenChange={(o) => !o && setArticle(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="article-dialog">
          {article && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{language === "hi" ? article.title_hi : article.title_en}</DialogTitle>
                <DialogDescription>{language === "hi" ? article.summary_hi : article.summary_en}</DialogDescription>
              </DialogHeader>
              <Markdown content={language === "hi" ? article.body_hi : article.body_en} />
              <Button className="mt-2 w-full gap-1.5 rounded-full" data-testid="article-ask-btn"
                onClick={() => { const q = language === "hi" ? article.title_hi : article.title_en; navigate(`/?ask=${encodeURIComponent(q)}&mode=understand`); }}>
                <Sparkles className="h-4 w-4" /> {t("launch_topic")}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
